import { useState, type FormEvent } from 'react';
import {
  ORGANIZATION_TYPES,
  PARTNERSHIP_KINDS,
  type OrganizationType,
  type PartnershipKind,
  type SubmitPartnershipInquiryRequest,
} from '@ghalishor/contracts';
import type { IdentityApiClient } from './auth/api-client';
import { identityErrorMessage } from './auth/api-client';
import { IDENTITY_WEB_ROUTES, type IdentityWebRoute } from './auth/web-flow';

const KIND_LABELS: Record<PartnershipKind, string> = {
  corporate_service: 'قرارداد شستشوی سازمانی',
  strategic_partner: 'همکاری راهبردی / شبکه',
  other: 'سایر',
};

const ORG_LABELS: Record<OrganizationType, string> = {
  company: 'شرکت',
  organization: 'سازمان',
  hotel: 'هتل',
  complex: 'مجتمع',
  government: 'اداره / دولتی',
  other: 'سایر',
};

export function PartnersPage(props: {
  readonly api: IdentityApiClient;
  readonly Shell: React.ComponentType<{
    title: string;
    description: string;
    children: React.ReactNode;
    onAccount?: () => void;
    eyebrow?: string;
  }>;
  readonly onNavigate: (route: IdentityWebRoute) => void;
}) {
  const [partnershipKind, setPartnershipKind] = useState<PartnershipKind>('corporate_service');
  const [organizationType, setOrganizationType] = useState<OrganizationType>('company');
  const [organizationName, setOrganizationName] = useState('');
  const [contactFullName, setContactFullName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [volume, setVolume] = useState('');
  const [message, setMessage] = useState('');
  const [preferredContactTime, setPreferredContactTime] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trackingCode, setTrackingCode] = useState<string | null>(null);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const body: SubmitPartnershipInquiryRequest = {
      partnershipKind,
      organizationType,
      organizationName: organizationName.trim(),
      contactFullName: contactFullName.trim(),
      mobile: mobile.trim(),
      email: email.trim() === '' ? null : email.trim(),
      city: city.trim(),
      approximateMonthlyVolume: volume.trim() === '' ? null : Number(volume),
      message: message.trim(),
      preferredContactTime:
        preferredContactTime.trim() === '' ? null : preferredContactTime.trim(),
    };
    void props.api
      .submitPartnershipInquiry(body)
      .then((response) => {
        setTrackingCode(response.trackingCode);
      })
      .catch((err: unknown) => setError(identityErrorMessage(err)))
      .finally(() => setBusy(false));
  };

  return (
    <props.Shell
      title="همکاری شرکت‌ها و سازمان‌ها"
      description="اگر سازمان یا مجموعه‌ای هستید و می‌خواهید با قالیشور همکاری کنید، درخواست خود را ثبت کنید تا تیم پلتفرم با شما تماس بگیرد."
      eyebrow="قالیشور · همکاری سازمانی"
    >
      <div className="partners-hero-note">
        قرارداد حجمی شستشو، همکاری راهبردی شبکه، یا پیشنهادهای سازمانی — از همین‌جا شروع می‌شود.
      </div>
      {trackingCode ? (
        <div className="message info partners-success">
          درخواست شما ثبت شد. کد پیگیری: <strong>{trackingCode}</strong>
          <div className="account-actions" style={{ marginTop: '1rem' }}>
            <button type="button" className="secondary" onClick={() => props.onNavigate(IDENTITY_WEB_ROUTES.home)}>
              بازگشت به خانه
            </button>
          </div>
        </div>
      ) : (
        <form className="identity-form partners-form" onSubmit={onSubmit}>
          <label>
            نوع همکاری
            <select
              value={partnershipKind}
              onChange={(event) => setPartnershipKind(event.target.value as PartnershipKind)}
            >
              {PARTNERSHIP_KINDS.map((key) => (
                <option key={key} value={key}>
                  {KIND_LABELS[key]}
                </option>
              ))}
            </select>
          </label>
          <label>
            نوع سازمان
            <select
              value={organizationType}
              onChange={(event) => setOrganizationType(event.target.value as OrganizationType)}
            >
              {ORGANIZATION_TYPES.map((key) => (
                <option key={key} value={key}>
                  {ORG_LABELS[key]}
                </option>
              ))}
            </select>
          </label>
          <label>
            نام سازمان / شرکت
            <input
              value={organizationName}
              onChange={(event) => setOrganizationName(event.target.value)}
              required
              minLength={2}
              maxLength={120}
            />
          </label>
          <label>
            نام تماس‌گیرنده
            <input
              value={contactFullName}
              onChange={(event) => setContactFullName(event.target.value)}
              required
              minLength={2}
              maxLength={80}
            />
          </label>
          <label>
            موبایل
            <input
              value={mobile}
              onChange={(event) => setMobile(event.target.value)}
              inputMode="tel"
              placeholder="09121234567"
              required
            />
          </label>
          <label>
            ایمیل (اختیاری)
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              type="email"
              maxLength={160}
            />
          </label>
          <label>
            شهر
            <input
              value={city}
              onChange={(event) => setCity(event.target.value)}
              required
              minLength={2}
              maxLength={80}
            />
          </label>
          <label>
            حجم تقریبی ماهانه (تخته)
            <input
              value={volume}
              onChange={(event) => setVolume(event.target.value)}
              inputMode="numeric"
              placeholder="مثلاً ۵۰"
            />
          </label>
          <label>
            زمان ترجیحی تماس (اختیاری)
            <input
              value={preferredContactTime}
              onChange={(event) => setPreferredContactTime(event.target.value)}
              maxLength={120}
              placeholder="مثلاً صبح‌ها تا ۱۲"
            />
          </label>
          <label>
            شرح همکاری مورد نظر
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              required
              minLength={20}
              maxLength={2000}
              rows={5}
              placeholder="نیاز، محدوده، حجم تقریبی و انتظارات خود را بنویسید…"
            />
          </label>
          {error ? <div className="message error">{error}</div> : null}
          <button type="submit" disabled={busy}>
            {busy ? 'در حال ارسال…' : 'ثبت درخواست همکاری'}
          </button>
          <button type="button" className="secondary" onClick={() => props.onNavigate(IDENTITY_WEB_ROUTES.home)}>
            بازگشت
          </button>
        </form>
      )}
    </props.Shell>
  );
}
